<template>
    <div ref="lottieContainer" :style="containerStyle" v-show="show"></div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, defineProps, defineExpose } from 'vue'
import lottie from 'lottie-web'

// Props - Keep it simple
const props = defineProps({
    animationData: {
        type: Object,
        required: true
    },
    width: {
        type: [String, Number],
        default: '100%'
    },
    height: {
        type: [String, Number],
        default: '100%'
    },
    autoplay: {
        type: Boolean,
        default: true
    },
    loop: {
        type: Boolean,
        default: true
    },
    speed: {
        type: Number,
        default: 1
    },
    show: {
        type: Boolean,
        default: true
    }
})

// Refs
const lottieContainer = ref(null)
const animation = ref(null)

// Computed
const containerStyle = computed(() => ({
    width: typeof props.width === 'number' ? `${props.width}px` : props.width,
    height: typeof props.height === 'number' ? `${props.height}px` : props.height,
    display: 'block'
}))

// Methods
const init = () => {
    if (!lottieContainer.value || !props.animationData) return

    animation.value = lottie.loadAnimation({
        container: lottieContainer.value,
        renderer: 'svg',
        loop: props.loop,
        autoplay: props.autoplay,
        animationData: props.animationData
    })

    if (props.speed !== 1) {
        animation.value.setSpeed(props.speed)
    }
}

const destroy = () => {
    if (animation.value) {
        animation.value.destroy()
        animation.value = null
    }
}

const play = () => {
    if (animation.value) animation.value.play()
}

const pause = () => {
    if (animation.value) animation.value.pause()
}

const stop = () => {
    if (animation.value) animation.value.stop()
}

// Watchers
watch(() => props.animationData, () => {
    destroy()
    nextTick(init)
})

watch(() => props.speed, (newSpeed) => {
    if (animation.value) animation.value.setSpeed(newSpeed)
})

// Lifecycle
onMounted(init)
onBeforeUnmount(destroy)

// Expose methods
defineExpose({
    play,
    pause,
    stop,
    animation
})
</script>